import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import Footer from "../../common/components/Footer";
import Nav from "../../common/components/Nav";
import ScrollReveal from "../../common/components/ScrollReveal";
import { fetchPublicPagesByType } from "../../utils/api/cms/page";
import { errorHandler } from "../../utils/helpers/errorHandler";

export const parseBodyToChartData = (htmlString) => {
  const result = {};
  if (!htmlString || !String(htmlString).trim()) return result;

  const toNumber = (raw) => {
    const cleaned = String(raw).replace(/[\s\u00a0,]/g, "");
    const match = cleaned.match(/-?\d+(?:\.\d+)?/);
    return match ? parseFloat(match[0]) : NaN;
  };

  const parser = new DOMParser();
  const doc = parser.parseFromString(String(htmlString), "text/html");

  // Try table format first
  const rows = doc.querySelectorAll("tr");
  rows.forEach((row) => {
    const cells = Array.from(row.querySelectorAll("td, th"));
    if (cells.length < 2) return;

    const texts = cells.map((cell) => cell.textContent.trim());
    let valueIndex = -1;
    for (let i = texts.length - 1; i >= 0; i -= 1) {
      if (texts[i] && !isNaN(toNumber(texts[i]))) {
        valueIndex = i;
        break;
      }
    }
    if (valueIndex < 1) return;

    const label = texts[valueIndex - 1];
    const value = toNumber(texts[valueIndex]);
    if (label && !isNaN(value)) result[label] = value;
  });
  if (Object.keys(result).length > 0) return result;

  // Fallback: paragraph format (label: value in same line)
  const paragraphs = doc.querySelectorAll("p");
  paragraphs.forEach((p) => {
    const text = p.textContent.trim();
    const colonIdx = text.indexOf(":");
    if (colonIdx === -1) return;

    const label = text.substring(0, colonIdx).trim();
    const value = toNumber(text.substring(colonIdx + 1));

    if (!isNaN(value) && label) result[label] = value;
  });
  if (Object.keys(result).length > 0) return result;

  // Fallback: line based format (div/li/br separated or plain text entries)
  const asLines = String(htmlString)
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|h[1-6]|tr|td|th|blockquote|figcaption)>/gi, "\n");
  const bodyText = parser.parseFromString(asLines, "text/html").body.textContent || "";
  const lines = bodyText
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  lines.forEach((line) => {
    const colonIdx = line.indexOf(":");
    if (colonIdx > 0) {
      const label = line.substring(0, colonIdx).trim();
      const value = toNumber(line.substring(colonIdx + 1));
      if (label && !isNaN(value)) {
        result[label] = value;
        return;
      }
    }

    const parts = line.split(/\s+/);
    const lastPart = parts[parts.length - 1];
    if (
      parts.length > 1 &&
      /^(?:[₦Nn?]\s*)?-?[\d.,]+$/.test(lastPart)
    ) {
      const value = toNumber(lastPart);
      const label = parts
        .slice(0, -1)
        .join(" ")
        .replace(/[:\-–—|]+$/, "")
        .trim();
      if (label && !isNaN(value)) result[label] = value;
    }
  });

  return result;
};

const formatBillions = (value) => {
  if (value >= 1_000_000_000) return `₦${(value / 1_000_000_000).toFixed(1)}B`;
  if (value >= 1_000_000) return `₦${(value / 1_000_000).toFixed(1)}M`;
  return `₦${value}`;
};

const formatExactNaira = (value) => `₦${Number(value).toLocaleString("en-NG")}`;

const CHART_CATEGORIES = [
  "Personnel Cost",
  "Overhead Cost",
  "Recurrent Expenditure",
  "Capital Expenditure",
  "Total Expenditure",
];

const COLORS = ["#0C9EA9", "#076f78", "#34c5d1", "#f4a261", "#e76f51", "#2a9d8f", "#264653"];

const BudgetPage = () => {
  const [publicBudgets, setPublicBudget] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSector, setSelectedSector] = useState("all");

  const budgetYear = localStorage.getItem("budgetYear") || new Date().getFullYear();
  const budgetText = localStorage.getItem("budgetText") || "Lagos State Comprehensive Budget";
  const yearLabel = localStorage.getItem("budgetYearLabel") || "Year";

  const handleFetchPublicBudgets = async (page = 0) => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchPublicPagesByType(page, "budgets");
      setPublicBudget(data.data.allPages);
    } catch (error) {
      console.error("Error fetching pages:", error);
      setError("Failed to load budgets. Please try again.");
      errorHandler(error, toast);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleFetchPublicBudgets(0);
  }, []);

  useEffect(() => {
    if (publicBudgets && publicBudgets.length > 0) {
      const allBudgets = publicBudgets.filter((p) => p.type === "budgets");
      setBudgets(allBudgets);
      setFiltered(allBudgets);
      console.log("Social Protection:", allBudgets.find(b => b.title === "Social Protection")?.body);
      allBudgets
        .filter((b) => /water/i.test(b.title || ""))
        .forEach((b) =>
          console.log("Waterways budget entry raw fields:", {
            title: b.title,
            body: b.body,
            budget: b.budget,
          })
        );
    }
  }, [publicBudgets]);

  useEffect(() => {
    if (search.trim() === "") {
      setFiltered(budgets);
    } else {
      setFiltered(
        budgets.filter((b) =>
          b.title.toLowerCase().includes(search.toLowerCase())
        )
      );
    }
  }, [search, budgets]);

  const buildChartData = () => {
    const sectorsToShow =
      selectedSector === "all"
        ? budgets
        : budgets.filter((b) => b.title === selectedSector);

    return CHART_CATEGORIES.map((category) => {
      const entry = { category: category.replace(" Expenditure", "").replace(" Cost", "") };
      sectorsToShow.forEach((budget) => {
        const parsed = parseBodyToChartData(budget.body || budget.budget || "");
        entry[budget.title] = parsed[category] || 0;
      });
      return entry;
    });
  };

  const chartData = buildChartData();
  const sectorsInChart =
    selectedSector === "all"
      ? budgets.map((b) => b.title)
      : [selectedSector];

  if (loading) {
    return (
      <div>
        <Nav />
        <div className="container text-center py-5">
          <div className="spinner-border" role="status" style={{ color: "#0C9EA9" }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading budget documents...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Nav />
        <div className="container text-center py-5">
          <div className="alert alert-danger">
            <h4>Error Loading Budgets</h4>
            <p>{error}</p>
            <button className="btn btn-primary mt-2" onClick={() => handleFetchPublicBudgets(0)}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <header>
        <Nav />
      </header>

      {/* Hero Banner */}
      <ScrollReveal>
      <section style={{ background: "linear-gradient(135deg, #0C9EA9 0%, #076f78 100%)", padding: "80px 0 60px" }}>
        <div className="container text-center text-white">
            <span style={{
              display: "inline-block",
              background: "rgba(255,255,255,0.15)",
              borderRadius: "20px",
              padding: "4px 16px",
              fontSize: "12px",
              fontWeight: "600",
              letterSpacing: "1.2px",
              marginBottom: "16px",
              textTransform: "uppercase",
            }}>
              {yearLabel} {budgetYear}
            </span>
            <h1 className="fw-bold mb-3" style={{ fontSize: "clamp(1.8rem, 4vw, 2.8rem)" }}>
              {budgetText}
            </h1>
            <p className="mb-4" style={{ opacity: 0.85, maxWidth: "520px", margin: "0 auto 24px" }}>
              Access and explore Lagos State's official budget documents in one place.
            </p>
            <div className="d-inline-flex align-items-center gap-2 px-4 py-2 rounded-pill"
              style={{ background: "rgba(255,255,255,0.15)" }}>
              <span style={{ fontSize: "1.4rem", fontWeight: "700" }}>{budgets.length}</span>
              <span style={{ opacity: 0.85, fontSize: "0.9rem" }}>
                Budget Document{budgets.length !== 1 ? "s" : ""} Available
              </span>
            </div>
        </div>
      </section>
      </ScrollReveal>

      {/* Search Bar */}
      <ScrollReveal>
      <section style={{ background: "#f8f9fa", borderBottom: "1px solid #e9ecef", padding: "20px 0" }}>
        <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-6 col-md-8">
                <div className="input-group">
                  <span className="input-group-text bg-white border-end-0" style={{ borderColor: "#dee2e6" }}>
                    <i className="fa fa-search text-muted"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control border-start-0 ps-0"
                    placeholder="Search budget documents..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ borderColor: "#dee2e6", boxShadow: "none" }}
                  />
                  {search && (
                    <button className="btn btn-outline-secondary border-start-0"
                      onClick={() => setSearch("")}
                      style={{ borderColor: "#dee2e6" }}>
                      ✕
                    </button>
                  )}
                </div>
              </div>
            </div>
        </div>
      </section>
      </ScrollReveal>

      {/* ── SECTION 1: Budget Cards ── */}
      <ScrollReveal>
      <section className="py-5 bg-white">
        <div className="container">
          {filtered.length > 0 ? (
            <div className="row g-4">
              {filtered.map((budget, idx) => {
                const parsed = parseBodyToChartData(budget.body || budget.budget || "");
                return (
                  <div className="col-xl-4 col-md-6 d-flex" key={budget.uuid || idx}>
                    <div className="h-100 w-100">
                      <ScrollReveal
                        className="h-100 w-100"
                        delayClass={`reveal-delay-${(idx % 3) + 1}`}
                      >
                        <div
                          className="card h-100 border-0 shadow-sm rounded-4 overflow-hidden bg-white d-flex flex-column"
                          style={{ transition: "transform 0.25s ease, box-shadow 0.25s ease" }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "translateY(-6px)";
                            e.currentTarget.style.boxShadow = "0 16px 40px rgba(0,0,0,0.13)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "";
                          }}
                        >
                          {budget?.banner && (
                            <div style={{
                              height: "180px",
                              backgroundImage: `url(${
                                budget.banner.startsWith("/")
                                  ? `${process.env.REACT_APP_ROOT_URL}${budget.banner}`
                                  : `${process.env.REACT_APP_ROOT_URL}/uploads/${budget.banner}`
                              })`,
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                            }} />
                          )}
                          <div style={{ height: "4px", background: "#0C9EA9" }} />
                          <div className="card-body p-4 d-flex flex-column">
                            <h5 className="fw-bold text-dark mb-3 fs-6">{budget.title}</h5>
                            <div className="flex-grow-1">
                              {Object.entries(parsed)
                                .filter(([label]) => label !== "Total Expenditure")
                                .map(([label, value]) => (
                                  <div
                                    key={label}
                                    className="d-flex justify-content-between align-items-center py-1"
                                    style={{ borderBottom: "1px solid #f0f0f0", fontSize: "13px" }}
                                  >
                                    <span className="text-secondary">{label}</span>
                                    <span className="fw-semibold text-dark" style={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                                      {formatExactNaira(value)}
                                    </span>
                                  </div>
                                ))}
                              {parsed["Total Expenditure"] && (
                                <div
                                  className="d-flex justify-content-between align-items-center mt-2 px-2 py-2 rounded-3"
                                  style={{ background: "#f0fafa", fontSize: "13px" }}
                                >
                                  <span className="fw-bold" style={{ color: "#0C9EA9" }}>Total Expenditure</span>
                                  <span className="fw-bold" style={{ color: "#0C9EA9", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                                    {formatExactNaira(parsed["Total Expenditure"])}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </ScrollReveal>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-5">
              <div style={{ fontSize: "3rem", marginBottom: "16px" }}>📂</div>
              <h4 className="text-dark fw-bold">
                {search ? "No results found" : "No budget documents available"}
              </h4>
              <p className="text-muted">
                {search
                  ? `No documents match "${search}". Try a different search term.`
                  : "There are no budget documents available at the moment."}
              </p>
              {search && (
                <button className="btn btn-outline-secondary mt-2" onClick={() => setSearch("")}>
                  Clear Search
                </button>
              )}
            </div>
          )}
        </div>
      </section>
      </ScrollReveal>

      {/* ── SECTION 2: Chart ── */}
      {budgets.length > 0 && (
        <ScrollReveal>
        <section className="py-5" style={{ background: "#f0fafa", borderTop: "1px solid #d0eeef" }}>
          <div className="container">
              <div className="mb-4">
                <h6 className="fw-bold mb-1" style={{ color: "#0C9EA9", fontSize: "12px", letterSpacing: "1.2px", textTransform: "uppercase" }}>
                  Expenditure Breakdown
                </h6>
                <h3 className="fw-bold text-dark mb-3">Budget Analytics</h3>
                <div className="d-flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedSector("all")}
                    className="btn btn-sm rounded-pill fw-semibold"
                    style={{
                      backgroundColor: selectedSector === "all" ? "#0C9EA9" : "transparent",
                      color: selectedSector === "all" ? "#fff" : "#0C9EA9",
                      border: "1.5px solid #0C9EA9",
                      transition: "all 0.2s",
                    }}
                  >
                    All Sectors
                  </button>
                  {budgets.map((budget) => (
                    <button
                      key={budget.id}
                      onClick={() => setSelectedSector(budget.title)}
                      className="btn btn-sm rounded-pill fw-semibold"
                      style={{
                        backgroundColor: selectedSector === budget.title ? "#0C9EA9" : "transparent",
                        color: selectedSector === budget.title ? "#fff" : "#0C9EA9",
                        border: "1.5px solid #0C9EA9",
                        transition: "all 0.2s",
                      }}
                    >
                      {budget.title}
                    </button>
                  ))}
                </div>
              </div>
              <ScrollReveal className="w-100">
                <div className="bg-white rounded-4 shadow-sm p-4">
                  <ResponsiveContainer width="100%" height={420}>
                    <BarChart data={chartData} margin={{ top: 10, right: 20, left: 20, bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="category" tick={{ fontSize: 12, fill: "#555" }} axisLine={false} tickLine={false} />
                      <YAxis tickFormatter={formatBillions} tick={{ fontSize: 11, fill: "#888" }} axisLine={false} tickLine={false} />
                      <Tooltip
                        formatter={(value, name) => [`₦${Number(value).toLocaleString()}`, name]}
                        contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 16px rgba(0,0,0,0.1)" }}
                      />
                      <Legend wrapperStyle={{ paddingTop: "16px", fontSize: "12px" }} />
                      {sectorsInChart.map((sector, i) => (
                        <Bar key={sector} dataKey={sector} fill={COLORS[i % COLORS.length]} radius={[4, 4, 0, 0]} maxBarSize={48} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </ScrollReveal>
          </div>
        </section>
        </ScrollReveal>
      )}

      {/* Back to Home */}
      <ScrollReveal>
      <section className="py-4 bg-light border-top">
        <div className="container text-center">
          <Link to="/" className="btn btn-outline-dark rounded-pill px-4 fw-semibold">
            ← Back to Homepage
          </Link>
        </div>
      </section>
      </ScrollReveal>

      <Footer />
    </div>
  );
};

export default BudgetPage;